import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { RecaptchaHandle } from 'shared/components/recaptcha'
import { BadRequest } from 'shared/errors/bad-request'
import { useToggle } from 'shared/hooks/use-toggle'
import { restorePassword } from 'modules/authentication/api/restore-password-api'
import { RestorePasswordInterface } from 'modules/authentication/types/restore-password-interface'

export const useRestorePasswordForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [isRecaptchaError, toggleRecaptchaError] = useToggle(false)
  const recaptchaRef = useRef<RecaptchaHandle>(null)

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<RestorePasswordInterface>()

  // Need for autocomplete in firefox
  useEffect(() => {
    document.addEventListener('animationstart', event => {
      if (event.animationName === 'onAutoFill') {
        const input = event.target as HTMLInputElement
        setValue(input.name as keyof RestorePasswordInterface, input.value)
      }
    })
  }, [])

  useEffect(() => {
    clearErrors()
  }, [router.locale])

  const onSubmit = async (values: RestorePasswordInterface) => {
    try {
      setIsLoading(true)
      await restorePassword(values)
      onSuccess()
    } catch (e) {
      if (e instanceof BadRequest) {
        const { fields, common } = e.errors || {}
        if (fields) {
          setError('email', { message: fields?.email.join('\n') })
        }
        if (common) {
          setError('root', { message: common?.join('\n') })
        }
        if (fields?.captcha) {
          recaptchaRef.current?.reset()
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    handleSubmit,
    control,
    setValue,
    errors,
    isLoading,
    onSubmit,
    isRecaptchaError,
    toggleRecaptchaError,
    recaptchaRef,
  }
}
